import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hotEvents } from "@/mocks/events";
import { SectionHeading, TrendBadge } from "@/components/base/Shared";
import { formatHeat } from "@/utils/format";

type SortKey = "heat" | "topic" | "repost";

const metrics: Record<SortKey, { label: string; get: (e: typeof hotEvents[number]) => number; fmt: (v: number) => string }> = {
  heat: { label: "热度值", get: (e) => e.heat, fmt: (v) => formatHeat(v) },
  topic: { label: "话题量", get: (e) => e.topicCount, fmt: (v) => `${v}万` },
  repost: { label: "转发量", get: (e) => e.repost, fmt: (v) => `${v}万` },
};

export default function HotRanking() {
  const [sortKey, setSortKey] = useState<SortKey>("heat");

  const list = useMemo(() => {
    const getter = metrics[sortKey].get;
    return [...hotEvents].sort((a, b) => getter(b) - getter(a)).slice(0, 8);
  }, [sortKey]);

  return (
    <section id="ranking" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="LIVE RANKING"
            title="实时热搜追踪榜"
            desc="按当前观测窗口的综合热度排序，同步记录话题量与转发量的变化方向。排名会随事件发酵状态动态调整。"
          />
          <div className="flex items-center gap-2 p-1 rounded-full bg-background-100 border border-background-200 shrink-0">
            {(Object.keys(metrics) as SortKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setSortKey(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  sortKey === key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                按{metrics[key].label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-background-200 overflow-hidden bg-background-50">
          <div className="hidden md:grid grid-cols-[56px_1fr_120px_130px_110px_120px] items-center px-5 py-3 bg-background-100 text-xs font-label text-foreground-500 tracking-wide">
            <span>排名</span>
            <span>事件</span>
            <span>分类</span>
            <span className="text-right">热度值</span>
            <span className="text-right">变化</span>
            <span className="text-right">话题 / 转发</span>
          </div>

          <ul className="divide-y divide-background-200">
            {list.map((event, index) => (
              <li key={event.id}>
                <Link
                  to={`/event/${event.id}`}
                  className="grid grid-cols-1 md:grid-cols-[56px_1fr_120px_130px_110px_120px] gap-3 md:gap-0 items-start md:items-center px-5 py-4 md:py-4 hover:bg-background-100 transition-colors group"
                >
                  <span className="flex items-center gap-2 md:block">
                    <span
                      className={`inline-flex w-8 h-8 rounded-lg items-center justify-center font-heading text-sm font-bold ${
                        index < 3
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                  </span>

                  <div className="min-w-0 md:pr-6">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {event.title}
                    </h3>
                    <p className="mt-1 text-xs md:text-sm text-foreground-500 line-clamp-1">
                      {event.summary}
                    </p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap md:hidden">
                      <span className="text-xs text-secondary-700 bg-secondary-50 px-2 py-0.5 rounded-md">
                        {event.category}
                      </span>
                      <span className="text-xs text-primary-600 font-label">
                        {formatHeat(event.heat)}
                      </span>
                      <TrendBadge trend={event.trend} rate={event.trendRate} />
                    </div>
                  </div>

                  <span className="hidden md:block">
                    <span className="text-xs text-secondary-700 bg-secondary-50 px-2.5 py-1 rounded-md whitespace-nowrap">
                      {event.category}
                    </span>
                  </span>

                  <span className="hidden md:block text-right font-label text-sm text-primary-600">
                    {formatHeat(event.heat)}
                  </span>

                  <span className="hidden md:flex justify-end">
                    <TrendBadge trend={event.trend} rate={event.trendRate} />
                  </span>

                  <span className="hidden md:block text-right text-xs text-foreground-600">
                    {event.topicCount}万 / {event.repost}万
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-900 text-sm font-label whitespace-nowrap transition-colors"
          >
            进入事件追踪库查看全部
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}